import * as React from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Box, Button, LinearProgress, Menu, MenuItem, Snackbar, Typography,
} from '@mui/material';
import { requirementsApi } from '../../services/api';
import iamApi from '../../services/iamApi';
import { usePref } from '../../services/prefs';
import { downloadTextFile, downloadBlob } from '../../utils/exportChat';
import { subtreeToCsv, buildPrintHtml, openPrintWindow } from '../../utils/documentExport';
import AddDialog from '../AddDialog';
import NewProjectDialog from '../dialogs/NewProjectDialog';
import ProjectDangerDialog from '../dialogs/ProjectDangerDialog';

/**
 * Project action buttons (#381) — New item / Copy project / Archive / Export,
 * mirroring the requirements-tree project right-click menu. Shared by BOTH the
 * project-detail panel (`ProjectDetails`, shown when a project node is selected)
 * and the `/analytics` dashboard (`DashboardHome`), so the two can't drift and a
 * fix lands in both places.
 *
 * Export (#381 follow-up) offers CSV (client-side), a genuine multi-worksheet
 * Excel (.xlsx) (server-rendered binary), PDF (print), Word (.docx binary) and
 * ReqIF. Every export shows a labelled indeterminate progress bar while the
 * request is in flight plus a success/error snackbar — the same
 * progress-feedback UX the tree right-click Markdown export uses, so a slow
 * (large-project) Word/Excel export no longer reads as "the button did nothing".
 * `onChanged` is called after a structural change so the host can reload.
 */
export default function ProjectActionBar({ projectId, projectName, onChanged, onError }) {
  const showHumanIds = usePref('showHumanIds');
  const [addOpen, setAddOpen] = React.useState(false);
  const [copyOpen, setCopyOpen] = React.useState(false);
  const [archiveOpen, setArchiveOpen] = React.useState(false);
  const [exportAnchor, setExportAnchor] = React.useState(null);
  // The label of the export currently in flight (drives the progress bar), or ''.
  const [exporting, setExporting] = React.useState('');
  const [snack, setSnack] = React.useState({ open: false, message: '', severity: 'info' });

  const showSnack = (message, severity = 'info') => setSnack({ open: true, message, severity });
  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));
  const fail = (e) => {
    const msg = e?.response?.data?.detail || e?.message || 'Action failed';
    showSnack(`Export failed: ${msg}`, 'error');
    if (onError) onError(msg);
  };
  const base = () => projectName || 'project';

  const handleNewItem = async (item) => {
    try { await requirementsApi.createItem(item); setAddOpen(false); if (onChanged) onChanged(); }
    catch (e) { if (onError) onError(e?.response?.data?.detail || e?.message || 'Action failed'); }
  };
  const handleCopyProject = (nm) => iamApi.copyProject(projectId, { name: nm, mode: 'full' })
    .then((res) => { if (onChanged) onChanged(); return res; });
  const handleArchiveProject = () => {
    iamApi.archiveProject(projectId)
      .then(() => { setArchiveOpen(false); if (onChanged) onChanged(); })
      .catch((e) => { setArchiveOpen(false); if (onError) onError(e?.response?.data?.detail || e?.message || 'Action failed'); });
  };

  // Run one export with shared progress feedback: close the menu, show the
  // in-flight bar + "Preparing…" snackbar, then success/error. `run` does the
  // fetch + download and resolves when the file has been handed to the browser.
  const runExport = (label, run) => {
    setExportAnchor(null);
    setExporting(label);
    showSnack(`Preparing ${label} export…`, 'info');
    Promise.resolve()
      .then(run)
      .then(() => showSnack(`${label} export downloaded`, 'success'))
      .catch(fail)
      .finally(() => setExporting(''));
  };

  const handleExportMarkdown = () => runExport('Markdown', () =>
    iamApi.exportProjectMarkdown(projectId)
      .then((data) => downloadTextFile(`${base()}.md`, data?.content || '', 'text/markdown')));
  const handleExportReqif = () => runExport('ReqIF', () =>
    iamApi.exportProjectReqif(projectId)
      .then((data) => downloadTextFile(`${base()}.reqif`, data?.content || '', 'application/xml')));
  const handleExportCsv = () => runExport('CSV', () =>
    iamApi.exportProjectJson(projectId)
      .then((data) => downloadTextFile(`${base()}.csv`,
        subtreeToCsv(data?.subtree || { children: [] }, { showHumanIds }), 'text/csv;charset=utf-8')));
  const handleExportExcel = () => runExport('Excel', () =>
    iamApi.exportProjectXlsx(projectId)
      .then((blob) => downloadBlob(`${base()}.xlsx`, blob)));
  const handleExportPdf = () => runExport('PDF', () =>
    iamApi.exportProjectJson(projectId)
      .then((data) => {
        const ok = openPrintWindow(buildPrintHtml(data?.subtree || { children: [] }, { showHumanIds }));
        if (!ok) throw new Error('Could not open the print view (popup blocked?).');
      }));
  const handleExportWord = () => runExport('Word', () =>
    iamApi.exportProjectDocx(projectId)
      .then((blob) => downloadBlob(`${base()}.docx`, blob)));

  const EXPORT_FORMATS = [
    { label: 'Markdown', testId: 'export-markdown', onClick: handleExportMarkdown },
    { label: 'CSV', testId: 'export-csv', onClick: handleExportCsv },
    { label: 'Excel (.xlsx)', testId: 'export-excel', onClick: handleExportExcel },
    { label: 'PDF', testId: 'export-pdf', onClick: handleExportPdf },
    { label: 'Word', testId: 'export-word', onClick: handleExportWord },
    { label: 'ReqIF', testId: 'export-reqif', onClick: handleExportReqif },
  ];

  if (!projectId) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-testid="project-actions">
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button size="small" variant="outlined" onClick={() => setAddOpen(true)}>New item</Button>
        <Button size="small" variant="outlined" onClick={() => setCopyOpen(true)}>Copy project</Button>
        <Button size="small" variant="outlined" color="error" onClick={() => setArchiveOpen(true)}>Archive</Button>
        <Button size="small" variant="outlined" data-testid="project-export-btn"
          disabled={!!exporting}
          onClick={(e) => setExportAnchor(e.currentTarget)}>
          Export ▾
        </Button>
        <Menu anchorEl={exportAnchor} open={!!exportAnchor} onClose={() => setExportAnchor(null)}>
          {EXPORT_FORMATS.map((f) => (
            <MenuItem key={f.label} data-testid={f.testId} onClick={f.onClick}>{f.label}</MenuItem>
          ))}
        </Menu>
      </div>

      {/* Labelled indeterminate progress while an export request is in flight
          (large Word/Excel exports can take a few seconds server-side). */}
      {exporting ? (
        <Box data-testid="export-progress" sx={{ maxWidth: 320 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }} data-testid="export-progress-label">
            {`Exporting ${exporting}…`}
          </Typography>
          <LinearProgress />
        </Box>
      ) : null}

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={closeSnack} severity={snack.severity} data-testid="export-snackbar"
          sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>

      <AddDialog open={addOpen} onClose={() => setAddOpen(false)} onAdd={handleNewItem} projectId={projectId || null} />
      <NewProjectDialog
        open={copyOpen}
        title={`Copy "${projectName || ''}"`}
        label="New project name"
        submitLabel="Copy project"
        onClose={() => setCopyOpen(false)}
        onSubmit={handleCopyProject}
        onDone={() => setCopyOpen(false)}
      />
      <ProjectDangerDialog
        open={archiveOpen}
        variant="archive"
        projectName={projectName || ''}
        onClose={() => setArchiveOpen(false)}
        onConfirm={handleArchiveProject}
      />
    </div>
  );
}

ProjectActionBar.propTypes = {
  projectId: PropTypes.string,
  projectName: PropTypes.string,
  onChanged: PropTypes.func,
  onError: PropTypes.func,
};
